// Deterministic, seedable RNG. All random rolls in the game go through this so
// that the server can log and later verify outcomes.

export interface RollRecord {
  label: string;
  p?: number;
  roll: number;
  ok?: boolean;
}

export class Rng {
  private s0: number;
  private s1: number;
  private s2: number;
  private s3: number;
  calls = 0;
  /** When set, labelled rolls are appended here (for audit logs). */
  log: RollRecord[] | null = null;

  constructor(seed: number | string) {
    let h = typeof seed === 'number' ? seed >>> 0 : hashString(seed);
    // splitmix32 to spread the seed over four words
    const next = () => {
      h = (h + 0x9e3779b9) | 0;
      let t = h ^ (h >>> 16);
      t = Math.imul(t, 0x21f0aaad);
      t = t ^ (t >>> 15);
      t = Math.imul(t, 0x735a2d97);
      return (t ^ (t >>> 15)) >>> 0;
    };
    this.s0 = next();
    this.s1 = next();
    this.s2 = next();
    this.s3 = next();
    if ((this.s0 | this.s1 | this.s2 | this.s3) === 0) this.s0 = 1;
  }

  /** xoshiro128** — returns a float in [0, 1). */
  next(): number {
    this.calls++;
    const result = Math.imul(rotl(Math.imul(this.s1, 5), 7), 9);
    const t = this.s1 << 9;
    this.s2 ^= this.s0;
    this.s3 ^= this.s1;
    this.s1 ^= this.s2;
    this.s0 ^= this.s3;
    this.s2 ^= t;
    this.s3 = rotl(this.s3, 11);
    return (result >>> 0) / 4294967296;
  }

  /** Uniform float in [min, max). */
  range(min: number, max: number): number {
    return min + (max - min) * this.next();
  }

  /** Uniform integer in [min, max] (inclusive). */
  int(min: number, max: number): number {
    return Math.floor(min + (max - min + 1) * this.next());
  }

  /** True with probability p. Labelled rolls are recorded when logging is on. */
  chance(p: number, label?: string): boolean {
    const roll = this.next();
    const ok = roll < p;
    if (label && this.log) this.log.push({ label, p: round3(p), roll: round3(roll), ok });
    return ok;
  }

  pick<T>(list: readonly T[]): T {
    return list[Math.floor(this.next() * list.length)];
  }

  /** Weighted pick from [value, weight] pairs. */
  weighted<T>(entries: readonly (readonly [T, number])[]): T {
    let total = 0;
    for (const [, w] of entries) total += Math.max(0, w);
    let r = this.next() * total;
    for (const [v, w] of entries) {
      r -= Math.max(0, w);
      if (r < 0) return v;
    }
    return entries[entries.length - 1][0];
  }

  shuffle<T>(list: T[]): T[] {
    for (let i = list.length - 1; i > 0; i--) {
      const j = Math.floor(this.next() * (i + 1));
      [list[i], list[j]] = [list[j], list[i]];
    }
    return list;
  }

  /** Derive an independent generator (e.g. one per tick) without consuming this one's stream order. */
  fork(label: string | number): Rng {
    return new Rng(`${this.s0}:${this.s1}:${this.s2}:${this.s3}:${label}`);
  }
}

function rotl(x: number, k: number): number {
  return (x << k) | (x >>> (32 - k));
}

export function hashString(str: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}

export function clamp(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v;
}

export function randomSeed(): number {
  return (Math.random() * 4294967296) >>> 0;
}
